// =============================================================================
// features/sales/campaigns/detail/tabs/OverviewTab.tsx — Aba "Visão geral":
// KPIs por status de carteira, progresso e alertas simples (doc 26 §6.7).
//
// Cada contagem vem de `GET /members?status=X&limit=1` (`total` real do
// servidor — mesmo padrão de CampaignsIndicators.tsx "Ativas"), NUNCA de uma
// soma feita no cliente sobre uma página parcial. `CampaignProgress` recebe
// `percent: null` (nenhum endpoint publica um percentual agregado ainda,
// mesma norma do componente) e um `label` textual com dois totais reais
// justapostos — não uma razão calculada.
// =============================================================================

import type { Campaign, CampaignMemberStatus } from '@elemento/shared-schemas';
import * as React from 'react';

import { Stat } from '../../../../../components/ui/Stat';
import { useCampaignMembersCount } from '../../../../../hooks/sales/useCampaignDetail';
import { CampaignProgress } from '../../components';

export interface OverviewTabProps {
  campaign: Campaign;
}

const STATUS_STATS: { status: CampaignMemberStatus; label: string }[] = [
  { status: 'available', label: 'Disponíveis' },
  { status: 'claimed', label: 'Em atendimento' },
  { status: 'waiting_callback', label: 'Aguardando retorno' },
  { status: 'completed', label: 'Concluídos' },
  { status: 'blocked', label: 'Bloqueados' },
  { status: 'removed', label: 'Removidos' },
];

export function OverviewTab({ campaign }: OverviewTabProps): React.JSX.Element {
  const total = useCampaignMembersCount(campaign.id, {});
  const completed = useCampaignMembersCount(campaign.id, { status: ['completed'] });

  const hasWindow = Object.keys(campaign.weeklyWindow).length > 0;
  const hasMembers = (total.total ?? 0) > 0;

  return (
    <div className="flex flex-col gap-6">
      {(!hasWindow || (!total.isLoading && !hasMembers)) && (
        <div className="flex flex-col gap-2">
          {!hasWindow && (
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-sm border"
              style={{
                background: 'var(--warning-bg)',
                borderColor: 'var(--warning)',
                borderLeftWidth: 3,
              }}
              role="alert"
            >
              <p className="font-sans text-sm text-ink">
                Sem janela de contato configurada — ninguém pode ser trabalhado até definir os
                dias/horários na aba Configuração.
              </p>
            </div>
          )}
          {!total.isLoading && !hasMembers && (
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-sm border"
              style={{
                background: 'var(--info-bg)',
                borderColor: 'var(--info)',
                borderLeftWidth: 3,
              }}
              role="alert"
            >
              <p className="font-sans text-sm text-ink">
                Nenhum membro na carteira ainda. Publique um público na criação/edição da campanha.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2 max-w-sm">
        <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink-3">
          Progresso
        </p>
        <CampaignProgress
          percent={null}
          {...(total.total !== undefined && completed.total !== undefined
            ? {
                label: `${completed.total.toLocaleString('pt-BR')} de ${total.total.toLocaleString('pt-BR')} trabalhados`,
              }
            : {})}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {STATUS_STATS.map(({ status, label }) => (
          <MemberStatusStat key={status} campaignId={campaign.id} status={status} label={label} />
        ))}
      </div>
    </div>
  );
}

function MemberStatusStat({
  campaignId,
  status,
  label,
}: {
  campaignId: string;
  status: CampaignMemberStatus;
  label: string;
}): React.JSX.Element {
  const { total, isLoading, isError } = useCampaignMembersCount(campaignId, { status: [status] });

  return (
    <Stat
      label={label}
      value={isError ? '—' : isLoading && total === undefined ? '…' : (total ?? '—')}
    />
  );
}
